import type { FieldTree } from '@angular/forms/signals';
import { validateStandardSchema } from '@angular/forms/signals';

import type {
    FormChatBotData,
    FormWidgetElement,
    FormWidgetEntity,
    FormWidgetRuleMessage,
    LegacyFormDefault,
    LegacyFormZero,
} from '@sites/entity-core';
import {
    FORM_WIDGET_RULE_MESSAGES,
    formLegacyDataRulesSchema,
    formLegacyDefaultV0Schema,
    formLegacyZeroV0Schema,
} from '@sites/entity-core';

import type { BaseLegacyEntityForm, BaseLegacyEntityFormOptions } from '../../core/create-base-legacy-entity-form';
import { configureCommonReadonly, createBaseLegacyEntityForm } from '../../core/create-base-legacy-entity-form';
import { arrayMove } from '../../core/array-move';
import { composeForm } from '../../core/compose-form';
import { extendedPositionPlugin, widgetPositionPlugin } from '../../plugins/widget/position.plugin';
import { visibilityPlugin } from '../../plugins/widget/visibility.plugin';
import { legacyWidgetEnvelopePlugin } from '../../plugins/widget/legacy-widget-envelope.plugin';

export type FormWidgetFormModel = FormWidgetEntity;
export type FormWidgetDataModel = FormWidgetEntity['data'];

/**
 * Subscription-form accessors shared verbatim by both variant assemblers — the
 * CRM/address-book fields, chat-bot and element arrays (with their immutable
 * array sugar), styling groups and view-mode flags. These are structurally
 * identical on both variants (only `position` differs, and that comes from the
 * position plugins), so the lazy `raw` thunk is narrowed to the default
 * variant's FieldTree internally — the button/social pattern.
 *
 * Exposed via getters so the FieldTree paths materialise lazily (after
 * `syncFromEntity` populates the model) — composed with {@link composeForm},
 * never object spread.
 *
 * **Beware optional branches**: `analyticsSettings` and per-element extras
 * may be absent from legacy
 * persisted data — FieldTree children only materialise for keys present in the
 * model, so such writes go **through the parent data node** (the missing-key
 * trap) and reads should prefer `value()`.
 */
function formSharedAccessors(raw: () => FieldTree<LegacyFormDefault> | FieldTree<LegacyFormZero>) {
    const r = raw as () => FieldTree<LegacyFormDefault>;
    return {
        // --- CRM integration ---
        get selectedAddressBook() {
            return r().data.selectedAddressBook;
        },
        setSelectedAddressBook: (value: FormWidgetDataModel['selectedAddressBook']) => {
            r().data.selectedAddressBook().value.set(value);
        },
        get selectedPipeline() {
            return r().data.selectedPipeline;
        },
        setSelectedPipeline: (value: FormWidgetDataModel['selectedPipeline']) => {
            r().data.selectedPipeline().value.set(value);
        },
        get selectedPipelineStep() {
            return r().data.selectedPipelineStep;
        },
        setSelectedPipelineStep: (value: FormWidgetDataModel['selectedPipelineStep']) => {
            r().data.selectedPipelineStep().value.set(value);
        },
        get selectedPipelineSettings() {
            return r().data.selectedPipelineSettings;
        },
        setSelectedPipelineSettings: (value: FormWidgetDataModel['selectedPipelineSettings']) => {
            r().data.selectedPipelineSettings().value.set(value);
        },
        get createContactAndDeal() {
            return r().data.createContactAndDeal;
        },
        setCreateContactAndDeal: (value: boolean) => {
            r().data.createContactAndDeal().value.set(value);
        },
        get dealName() {
            return r().data.dealName;
        },
        setDealName: (value: FormWidgetDataModel['dealName']) => {
            r().data.dealName().value.set(value);
        },

        // --- Address book / tags ---
        get createAddressBook() {
            return r().data.createAddressBook;
        },
        setCreateAddressBook: (value: boolean) => {
            r().data.createAddressBook().value.set(value);
        },
        get addCrmTags() {
            return r().data.addCrmTags;
        },
        setAddCrmTags: (value: boolean) => {
            r().data.addCrmTags().value.set(value);
        },
        get contactTags() {
            return r().data.contactTags;
        },
        setContactTags: (value: number[]) => {
            r().data.contactTags().value.set(value);
        },
        get addAddressBookTags() {
            return r().data.addAddressBookTags;
        },
        setAddAddressBookTags: (value: boolean) => {
            r().data.addAddressBookTags().value.set(value);
        },
        get contactAddressBookTags() {
            return r().data.contactAddressBookTags;
        },
        setContactAddressBookTags: (value: number[]) => {
            r().data.contactAddressBookTags().value.set(value);
        },

        // --- Chat bots ---
        get addChatBotToForm() {
            return r().data.addChatBotToForm;
        },
        setAddChatBotToForm: (value: boolean) => {
            r().data.addChatBotToForm().value.set(value);
        },
        get chatBots() {
            return r().data.chatBots;
        },
        setChatBots: (value: FormChatBotData[]) => {
            r().data.chatBots().value.set(value);
        },
        chatBotAt: (index: number): FieldTree<FormChatBotData> | undefined => {
            return (r().data.chatBots as unknown as Record<number, FieldTree<FormChatBotData>>)[index];
        },
        addChatBot: (item: FormChatBotData) => {
            r()
                .data.chatBots()
                .value.update((arr) => [...arr, item]);
        },
        removeChatBot: (index: number) => {
            r()
                .data.chatBots()
                .value.update((arr) => arr.filter((_, i) => i !== index));
        },
        moveChatBot: (from: number, to: number) => {
            r()
                .data.chatBots()
                .value.update((arr) => arrayMove(arr, from, to));
        },
        updateChatBot: (index: number, patch: Partial<FormChatBotData>) => {
            r()
                .data.chatBots()
                .value.update((arr) => arr.map((b, i) => (i === index ? { ...b, ...patch } : b)));
        },
        get chatBotsButtonSettings() {
            return r().data.chatBotsButtonSettings;
        },
        setChatBotsButtonSettings: (value: FormWidgetDataModel['chatBotsButtonSettings']) => {
            r().data.chatBotsButtonSettings().value.set(value);
        },

        // --- Form content ---
        get elements() {
            return r().data.elements;
        },
        setElements: (value: FormWidgetElement[]) => {
            r().data.elements().value.set(value);
        },
        elementAt: (index: number): FieldTree<FormWidgetElement> | undefined => {
            return (r().data.elements as unknown as Record<number, FieldTree<FormWidgetElement>>)[index];
        },
        addElement: (item: FormWidgetElement) => {
            r()
                .data.elements()
                .value.update((arr) => [...arr, item]);
        },
        removeElement: (index: number) => {
            r()
                .data.elements()
                .value.update((arr) => arr.filter((_, i) => i !== index));
        },
        moveElement: (from: number, to: number) => {
            r()
                .data.elements()
                .value.update((arr) => arrayMove(arr, from, to));
        },
        updateElement: (index: number, patch: Partial<FormWidgetElement>) => {
            r()
                .data.elements()
                .value.update((arr) => arr.map((e, i) => (i === index ? { ...e, ...patch } : e)));
        },

        // --- Styling groups (FieldTree branch + whole-value setter) ---
        get inputSettings() {
            return r().data.inputSettings;
        },
        setInputSettings: (value: FormWidgetDataModel['inputSettings']) => {
            r().data.inputSettings().value.set(value);
        },
        get formButtonSettings() {
            return r().data.formButtonSettings;
        },
        setFormButtonSettings: (value: FormWidgetDataModel['formButtonSettings']) => {
            r().data.formButtonSettings().value.set(value);
        },
        get modalFormButtonSettings() {
            return r().data.modalFormButtonSettings;
        },
        setModalFormButtonSettings: (value: FormWidgetDataModel['modalFormButtonSettings']) => {
            r().data.modalFormButtonSettings().value.set(value);
        },
        get additionalFormSettings() {
            return r().data.additionalFormSettings;
        },
        setAdditionalFormSettings: (value: FormWidgetDataModel['additionalFormSettings']) => {
            r().data.additionalFormSettings().value.set(value);
        },
        /** Optional branch — may be absent on legacy data; prefer `value()` reads. */
        get analyticsSettings() {
            return r().data.analyticsSettings;
        },
        setAnalyticsSettings: (value: FormWidgetDataModel['analyticsSettings']) => {
            // Whole-data write: the branch is optional, so its FieldTree child
            // may not exist yet (the missing-key trap) — writing through the
            // parent both sets the value and materialises the child.
            r()
                .data()
                .value.update((data) => ({ ...data, analyticsSettings: value }));
        },

        // --- View mode ---
        get formView() {
            return r().data.formView;
        },
        setFormView: (value: FormWidgetDataModel['formView']) => {
            r().data.formView().value.set(value);
        },
        get isTwoStepForm() {
            return r().data.isTwoStepForm;
        },
        setIsTwoStepForm: (value: boolean) => {
            r().data.isTwoStepForm().value.set(value);
        },
        get modalTitle() {
            return r().data.modalTitle;
        },
        setModalTitle: (value: FormWidgetDataModel['modalTitle']) => {
            r().data.modalTitle().value.set(value);
        },
        get isSynchronizeStyle() {
            return r().data.isSynchronizeStyle;
        },
        setIsSynchronizeStyle: (value: boolean) => {
            r().data.isSynchronizeStyle().value.set(value);
        },

        // --- Publish checks ---
        /**
         * Every failed publish check of the form, one translation key per issue. The same
         * `formLegacyDataRulesSchema` errors also sit on the fields they name
         * (`form.dealName().errors()`, `form.elementAt(i)?.().errors()`).
         */
        get errors(): FormWidgetRuleMessage[] {
            return r()
                .data()
                .errorSummary()
                .map((error) => error.message)
                .filter((message): message is FormWidgetRuleMessage =>
                    FORM_WIDGET_RULE_MESSAGES.includes(message as FormWidgetRuleMessage)
                );
        },
    };
}

/**
 * Per-variant accessor assemblers. Exactly like button/social, the two form
 * variants share every accessor and differ **only** in the `position` branch
 * type: the default form's grid is the simple per-device `WidgetPosition`
 * (via {@link widgetPositionPlugin}), the zero form's is the full
 * `ExtendedPosition` (anchors, heights, changed-flags — via
 * {@link extendedPositionPlugin}).
 *
 * The first `composeForm` part widens the inherited base methods
 * (`syncFromEntity`, `setValue`, `value`) to operate on `FormWidgetEntity` so
 * the dispatcher's union return collapses into a single useful shape for
 * callers that only know the union (e.g. `WebLegacyWidgetSignalFormService`).
 */
function assembleDefaultFormWidget(base: BaseLegacyEntityForm<LegacyFormDefault>) {
    // `_raw` stays typed as `FieldTree<LegacyFormDefault>` so each accessor
    // resolves to the correct nested `FieldTree<…>`.
    const _raw = base._raw;

    // Checked widening (no cast): the variant form is assignable to the
    // entity-union form, so the dispatcher's union return collapses into a
    // single useful shape for union-only callers.
    const widenedBase: BaseLegacyEntityForm<FormWidgetEntity> = base;

    return composeForm(
        widenedBase,
        legacyWidgetEnvelopePlugin(() => _raw),
        formSharedAccessors(() => _raw),
        visibilityPlugin(() => _raw.data),
        // Default-only layout — the simple per-device position grid.
        widgetPositionPlugin(() => _raw.data)
    );
}

function assembleZeroFormWidget(base: BaseLegacyEntityForm<LegacyFormZero>) {
    // See {@link assembleDefaultFormWidget} — `_raw` is the narrow
    // `FieldTree<LegacyFormZero>`, which gives `position` its correct
    // `FieldTree<ExtendedPosition>` type.
    const _raw = base._raw;

    const widenedBase: BaseLegacyEntityForm<FormWidgetEntity> = base;

    return composeForm(
        widenedBase,
        legacyWidgetEnvelopePlugin(() => _raw),
        formSharedAccessors(() => _raw),
        visibilityPlugin(() => _raw.data),
        // Zero-only layout — the full extended-position grid.
        extendedPositionPlugin(() => _raw.data)
    );
}

/**
 * Signal-form view of a **default-placement** subscription-form widget.
 * Validates against the legacy default envelope schema, which permits an
 * absent `type` field for parity with pre-discriminator persisted data on disk.
 *
 * **Field accessors are FieldTree references** — exposed via getters so the
 * FieldTree paths materialise lazily (after `syncFromEntity` populates the
 * model). Consumers call them to get the `FieldState`, then read `.value()`,
 * `.touched()`, `.dirty()`, `.errors()` as needed. Convenience `setX(v)`
 * methods are sugar over `field().value.set(v)`; the `elements` / `chatBots`
 * arrays add add/remove/move/update/`xAt(i)` sugar on top.
 */
export function createLegacyFormDefaultForm(options?: BaseLegacyEntityFormOptions<LegacyFormDefault>) {
    const base = createBaseLegacyEntityForm<LegacyFormDefault>((schemaPath) => {
        validateStandardSchema(schemaPath, formLegacyDefaultV0Schema);
        validateStandardSchema(schemaPath.data, formLegacyDataRulesSchema);
        configureCommonReadonly(schemaPath);
    }, options);

    // The assembler takes the narrow `BaseLegacyEntityForm<LegacyFormDefault>`
    // so its internal `_raw` keeps the precise FieldTree shape; the public
    // widening to `FormWidgetEntity` happens inside the assembler, via a
    // checked assignment on the base part.
    return assembleDefaultFormWidget(base);
}

/**
 * Signal-form view of a **zero-block-placement** subscription-form widget.
 * Validates against the legacy zero envelope schema, which requires
 * `type: 'zero'` on the envelope (the host-app factory copies `meta.type` onto
 * the entity at creation time).
 *
 * Diverges from {@link createLegacyFormDefaultForm} only on `position`: the
 * zero form's grid is the full `ExtendedPosition` (per-device
 * width/height/x/y/rotate/zIndex plus anchor sides) instead of the default
 * variant's simple `WidgetPosition`.
 */
export function createLegacyFormZeroForm(options?: BaseLegacyEntityFormOptions<LegacyFormZero>) {
    const base = createBaseLegacyEntityForm<LegacyFormZero>((schemaPath) => {
        validateStandardSchema(schemaPath, formLegacyZeroV0Schema);
        validateStandardSchema(schemaPath.data, formLegacyDataRulesSchema);
        configureCommonReadonly(schemaPath);
    }, options);

    // See `createLegacyFormDefaultForm` — narrow base in, widening happens
    // inside the assembler.
    return assembleZeroFormWidget(base);
}

/**
 * Variant dispatcher. Callers that only know `FormWidgetEntity` (the union of
 * default + zero) use this — `WebLegacyWidgetSignalFormService` for instance — and
 * the dispatcher reads the variant tag from `options.initialData?.type` to
 * pick the matching per-variant factory.
 *
 * The return type is the **union** of the two variant forms. Consumers that
 * want to read variant-specific accessors (the `position` branch types
 * diverge) must narrow via `form.value().type === 'zero'` first, or use the
 * variant factories directly to skip narrowing.
 */
export function createFormWidgetForm(options?: BaseLegacyEntityFormOptions<FormWidgetEntity>) {
    const variant = options?.initialData?.type;

    if (variant === 'zero') {
        return createLegacyFormZeroForm(options as BaseLegacyEntityFormOptions<LegacyFormZero>);
    }

    return createLegacyFormDefaultForm(options as BaseLegacyEntityFormOptions<LegacyFormDefault>);
}

export type FormWidgetForm = ReturnType<typeof createFormWidgetForm>;
export type FormWidgetDefaultForm = ReturnType<typeof createLegacyFormDefaultForm>;
export type FormWidgetZeroForm = ReturnType<typeof createLegacyFormZeroForm>;
